import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
  Stack,
  Button,
  Badge,
  Tooltip,
  Container,
  Pagination
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Subject as SubjectIcon,
  Message as MessageIcon,
  FilterList as FilterIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CalendarToday as DateIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

export const DashboardLayout = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // State للداتا
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State للـ Pagination
  const [page, setPage] = useState(1); // MUI Pagination بيبدأ من 1
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // State للـ Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('-createdAt');
  const [activeFilters, setActiveFilters] = useState({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch clients
  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      params.append('page', page);
      params.append('limit', rowsPerPage);
      
      if (sortBy) {
        params.append('sort', sortBy);
      }
      
      if (debouncedSearchTerm) {
        params.append('keyword', debouncedSearchTerm);
      }
      
      if (filterStatus !== 'all') {
        params.append('isActive', filterStatus);
      }

      console.log('Fetching with params:', params.toString());

      const response = await fetch(`https://crmapitest4.onrender.com/api/v1/clients?${params}`);
      const data = await response.json();

      console.log('Response:', data);

      if (response.ok) {
        setClients(data.data.clients || []);
        setTotalCount(data.pagination?.totalItems || data.results || 0);
        setTotalPages(data.pagination?.totalPages || 1);
        
        const filters = {};
        if (debouncedSearchTerm) filters.search = true;
        if (filterStatus !== 'all') filters.status = true;
        if (sortBy !== '-createdAt') filters.sort = true;
        setActiveFilters(filters);
        
      } else {
        throw new Error(data.message || 'فشل في جلب البيانات');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, filterStatus, debouncedSearchTerm]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleFilterMenuOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userEmail');
    navigate('/Login');
  };

  // Pagination handlers
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1);
  };

  // Search handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPage(1);
  };

  // Filter handlers
  const handleFilterChange = (event) => {
    setFilterStatus(event.target.value);
    setPage(1);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleRefresh = () => {
    setPage(1);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterStatus('all');
    setSortBy('-createdAt');
    fetchClients();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return format(date, 'yyyy/MM/dd');
  };

  // Mobile Card View
  const MobileCard = ({ client }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        sx={{ 
          mb: 2, 
          borderRadius: '15px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.2)',
          }
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#667eea', width: 48, height: 48 }}>
                {client.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                  {client.name || 'غير محدد'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {client.createdAt ? formatDate(client.createdAt) : 'تاريخ غير معروف'}
                </Typography>
              </Box>
              <Chip
                label={client.isActive ? 'نشط' : 'غير نشط'}
                size="small"
                color={client.isActive ? 'success' : 'default'}
                icon={client.isActive ? <ActiveIcon /> : <InactiveIcon />}
                sx={{ borderRadius: '8px', fontWeight: 500 }}
              />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmailIcon sx={{ color: '#667eea', fontSize: 20 }} />
              <Typography variant="body2">
                {client.email || 'لا يوجد بريد'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ color: '#667eea', fontSize: 20 }} />
              <Typography variant="body2" dir="ltr">
                {client.phonecode || ''} {client.phonenumber || 'لا يوجد رقم'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SubjectIcon sx={{ color: '#667eea', fontSize: 20 }} />
              <Typography variant="body2" fontWeight="500">
                {client.subject || 'لا يوجد موضوع'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <MessageIcon sx={{ color: '#667eea', fontSize: 20, mt: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                {client.message || 'لا توجد رسالة'}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5',marginTop:'20px' }}>
      {/* Navbar علوي فقط */}
      <AppBar
        position="fixed"
        sx={{
          width: '100%',
          bgcolor: 'white',
          color: '#1a1a2e',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <PeopleIcon sx={{ color: '#667eea', fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              إدارة العملاء
            </Typography>
          </Box>
          
          {/* Filter Badge */}
          <Tooltip title="عدد الفلاتر النشطة">
            <IconButton 
              onClick={handleFilterMenuOpen}
              sx={{ mr: 1 }}
            >
              <Badge 
                badgeContent={Object.keys(activeFilters).length} 
                color="primary"
              >
                <FilterIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          
          <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: '#667eea' }}>
              {localStorage.getItem('userEmail')?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
          </IconButton>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">
                {localStorage.getItem('userEmail')}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              تسجيل الخروج
            </MenuItem>
          </Menu>

          {/* Filter Menu */}
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={handleFilterMenuClose}
            onClick={handleFilterMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">الفلاتر النشطة:</Typography>
            </MenuItem>
            {Object.keys(activeFilters).length > 0 ? (
              Object.keys(activeFilters).map(key => (
                <MenuItem key={key} disabled>
                  <Typography variant="caption" color="primary">
                    {key === 'search' && '🔍 بحث'}
                    {key === 'status' && '⚡ تصفية'}
                    {key === 'sort' && '📊 ترتيب'}
                  </Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <Typography variant="caption">لا توجد فلاتر نشطة</Typography>
              </MenuItem>
            )}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* المحتوى الرئيسي */}
      <Container 
        maxWidth="xl" 
        sx={{ 
          pt: '100px', 
          pb: 4 
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper 
                sx={{ 
                  p: 2, 
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}
              >
                <Typography variant="subtitle2">إجمالي العملاء</Typography>
                <Typography variant="h4">{totalCount}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper 
                sx={{ 
                  p: 2, 
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white'
                }}
              >
                <Typography variant="subtitle2">الصفحة الحالية</Typography>
                <Typography variant="h4">{page} / {totalPages || 1}</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper 
                sx={{ 
                  p: 2, 
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  color: 'white'
                }}
              >
                <Typography variant="subtitle2">عرض</Typography>
                <Typography variant="h4">{rowsPerPage} عميل</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Search and Filters */}
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: '15px',
              background: 'white'
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="🔍 بحث بالاسم أو البريد الإلكتروني..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton onClick={clearSearch} edge="end" size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>الحالة</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={handleFilterChange}
                    label="الحالة"
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="all">الكل</MenuItem>
                    <MenuItem value="true">نشط فقط</MenuItem>
                    <MenuItem value="false">غير نشط فقط</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} md={3}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>ترتيب حسب</InputLabel>
                  <Select
                    value={sortBy}
                    onChange={handleSortChange}
                    label="ترتيب حسب"
                    sx={{ borderRadius: '12px' }}
                  >
                    <MenuItem value="-createdAt">الأحدث أولاً</MenuItem>
                    <MenuItem value="createdAt">الأقدم أولاً</MenuItem>
                    <MenuItem value="name">الاسم (أ - ي)</MenuItem>
                    <MenuItem value="-name">الاسم (ي - أ)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{ borderRadius: '8px' }}
                  >
                    إعادة تعيين
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* البيانات */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert 
              severity="error" 
              sx={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(255,0,0,0.1)'
              }}
            >
              {error}
            </Alert>
          ) : clients.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '15px' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                لا توجد بيانات
              </Typography>
              <Typography variant="body2" color="text.secondary">
                لا يوجد عملاء متطابقين مع معايير البحث
              </Typography>
            </Paper>
          ) : (
            <AnimatePresence>
              {isTablet ? (
                <Box>
                  {clients.map((client) => (
                    <MobileCard key={client._id} client={client} />
                  ))}
                </Box>
              ) : (
                <TableContainer 
                  component={Paper} 
                  sx={{ 
                    borderRadius: '15px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}
                >
                  <Table>
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>الاسم</TableCell>
                        <TableCell>البريد الإلكتروني</TableCell>
                        <TableCell>رقم الهاتف</TableCell>
                        <TableCell>الموضوع</TableCell>
                        <TableCell>الحالة</TableCell>
                        <TableCell>تاريخ التسجيل</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clients.map((client, index) => (
                        <TableRow 
                          key={client._id} 
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: 'rgba(102, 126, 234, 0.05)',
                            }
                          }}
                        >
                          <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Typography fontWeight="500">
                              {client.name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>{client.email || '-'}</TableCell>
                          <TableCell dir="ltr">
                            {client.phonecode || ''} {client.phonenumber || '-'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={client.subject || ''}>
                              <Typography 
                                noWrap 
                                sx={{ maxWidth: 200 }}
                              >
                                {client.subject || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={client.isActive ? 'نشط' : 'غير نشط'}
                              size="small"
                              color={client.isActive ? 'success' : 'default'}
                              icon={client.isActive ? <ActiveIcon /> : <InactiveIcon />}
                              sx={{ borderRadius: '8px' }}
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(client.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AnimatePresence>
          )}

          {/* Pagination Controls - زي المطلوب */}
          {!loading && !error && clients.length > 0 && (
            <Paper sx={{ 
              p: isMobile ? 1.5 : 2, 
              mt: 2, 
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
            }}>
              <Grid container alignItems="center" justifyContent="space-between" spacing={isMobile ? 1 : 2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>عدد العناصر في الصفحة</InputLabel>
                    <Select
                      value={rowsPerPage}
                      label="عدد العناصر في الصفحة"
                      onChange={handleLimitChange}
                      sx={{ 
                        borderRadius: 1.5,
                        fontSize: isMobile ? '0.8rem' : '0.875rem'
                      }}
                    >
                      {/* <MenuItem value={1} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>1</MenuItem> */}
                      <MenuItem value={5} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>5</MenuItem>
                      <MenuItem value={10} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>10</MenuItem>
                      <MenuItem value={20} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>20</MenuItem>
                      <MenuItem value={50} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: 2,
                          fontSize: isMobile ? '0.8rem' : '0.875rem'
                        }
                      }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary" align="right" sx={{ 
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}>
                    عرض {(page - 1) * rowsPerPage + 1} إلى {Math.min(page * rowsPerPage, totalCount)} من {totalCount} عميل
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Footer Summary */}
          {!loading && !error && clients.length > 0 && (
            <Box sx={{ 
              mt: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ 
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}>
                عرض <strong>{clients.length}</strong> من <strong>{totalCount}</strong> عميل
                {searchTerm && ` مطابق لـ "${searchTerm}"`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ 
                fontSize: isMobile ? '0.65rem' : '0.75rem'
              }}>
                آخر تحديث: {new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          )}
        </motion.div>
      </Container>
    </Box>
  );
};

export default DashboardLayout;